import { useToast } from '@/hooks/useToast';
import { Inventory2Outlined, ManageSearchOutlined, MemoryOutlined, TagOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { ReactNode } from 'react';
import { usePartFinderSearch } from './usePartFinderSearch';

interface Example {
  format: string;
  value: string;
  icon: ReactNode;
}

const EXAMPLES: Example[] = [
  { format: 'NSN', value: '5841-01-528-4987', icon: <Inventory2Outlined /> },
  { format: 'NIIN', value: '001715042', icon: <TagOutlined /> },
  { format: 'Part Number', value: '813R250G01', icon: <MemoryOutlined /> },
];

const Panel = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 360,
  maxWidth: '1200px',
  marginTop: '32px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: '48px 24px',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
}));

const IconBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 64,
  height: 64,
  borderRadius: '14px',
  marginBottom: '16px',
  backgroundColor: alpha(theme.palette.secondary.main, 0.14),
  color: theme.palette.secondary.main,
  '& svg': {
    fontSize: 32,
  },
}));

const Heading = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  marginTop: '4px',
  marginBottom: '24px',
  maxWidth: 420,
}));

const ExamplesGrid = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: '12px',
});

const ExampleCard = styled('button')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '6px',
  width: 200,
  padding: '14px 16px',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  transition: 'border-color 0.15s ease, transform 0.15s ease',
  '&:hover': {
    borderColor: theme.palette.secondary.main,
    transform: 'translateY(-2px)',
  },
  '&:focus-visible': {
    outline: 'none',
    borderColor: theme.palette.secondary.main,
  },
  '&:disabled': {
    cursor: 'default',
    opacity: 0.6,
    transform: 'none',
    borderColor: theme.palette.divider,
  },
}));

const ExampleIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  color: theme.palette.secondary.main,
  '& svg': {
    fontSize: 20,
  },
}));

const ExampleFormat = styled('span')(({ theme }) => ({
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
}));

const ExampleValue = styled('span')(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

export const PartFinderZeroState = () => {
  const { runSearch, isPending } = usePartFinderSearch();
  const { showToast } = useToast();

  const handleExample = async (value: string) => {
    try {
      await runSearch({ partNumber: value });
    } catch {
      showToast({ message: 'Could not start that search. Please try again.', type: 'error' });
    }
  };

  return (
    <Panel>
      <IconBadge>
        <ManageSearchOutlined />
      </IconBadge>
      <Heading>No searches yet</Heading>
      <Subtitle>Enter a part number above to generate a sourcing report, or start with one of these examples.</Subtitle>
      <ExamplesGrid>
        {EXAMPLES.map(example => (
          <ExampleCard
            key={example.value}
            type='button'
            onClick={() => void handleExample(example.value)}
            disabled={isPending}
          >
            <ExampleIcon>{example.icon}</ExampleIcon>
            <ExampleFormat>{example.format}</ExampleFormat>
            <ExampleValue>{example.value}</ExampleValue>
          </ExampleCard>
        ))}
      </ExamplesGrid>
    </Panel>
  );
};
